import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import axios from "axios";

import { LoaderContext } from "../../context/LoaderContext";
import Loader from "../../components/Loader";
import { __API_URL__ } from "../../SERVER_URL";
import { trialPatientSchema } from "../../schemas";
import useUserActionsLogsMutation from "../../hooks/useUserActionsLogsMutation";
import useGetAvailableLanguages from "../../hooks/useGetAvailableLanguages";
import { CustomDatePicker } from "../UI/CustomDatePicker";
import { MuiTelInput } from "mui-tel-input";

dayjs.extend(utc);

const AddNewPatientModal = ({ modalShow, trial_id = null }) => {
  const token = localStorage.getItem("token");
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const loaderContext = useContext(LoaderContext);
  const navigate = useNavigate();

  const [trialsList, setTrialsList] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const { data: languages = [] } = useGetAvailableLanguages();
  const [enforceStartDate, setEnforceStartDate] = useState(true);
  const [dateTime, setDateTime] = useState(dayjs());
  const [isManual, setIsManual] = useState(false);
  const [hasScreening, setHasScreening] = useState(false);
  const [trialName, setTrialName] = useState("");

  const logAction = useUserActionsLogsMutation();

  const initialValues = {
    email: "",
    confirm_email: "",
    first_name: "",
    middle_name_initial: "",
    last_name: "",
    birth_date: "",
    phone: "",
    preferred_language: "en",
    trial_id: "",
    participant_number: "",
    start_date: "",
  };

  const {
    values,
    errors,
    touched,
    handleBlur,
    handleChange,
    handleSubmit,
    setFieldValue,
    isSubmitting,
  } = useFormik({
    initialValues: initialValues,
    validationSchema: trialPatientSchema,
    onSubmit: async (values, helpers) => {
      try {
        const payload = {
          ...values,
        };

        if (isManual && payload.participant_number === "") {
          helpers.setErrors({
            participant_number: "Subject number is required",
          });

          return;
        }

        if (payload.middle_name_initial.trim() === "") {
          payload.middle_name_initial = "-";
        }

        const res = await axios.post(`${__API_URL__}/api/patient/patient/add_patient/`, payload, {
          headers: { Authorization: `Token ${token}` },
        });

        setSubmitted(true);
        toast.success("Subject successfully added", {
          autoClose: 1500,
        });
        logAction.mutate({
          action_type: `Fill the form and Subject Added Successfully`,
          trial_name: res.data.trial_name,
          patient_id: res.data.email,
        });

        setTimeout(() => navigate(`/patient/${res.data.id}/?trial=${res.data.trial_id}`), 2000);
      } catch (error) {
        helpers.setErrors({
          submit: error.response?.data?.message || error.message,
        });
        toast.error(error.response?.data?.message || "Error adding patient");
      }
    },
  });

  useEffect(() => {
    if (!dateTime || !dateTime.isValid()) return;

    setFieldValue("start_date", dateTime.toISOString());
  }, [dateTime]);

  useEffect(() => {
    setDateTime(enforceStartDate ? dayjs().startOf("day") : dayjs());
  }, [enforceStartDate]);

  const handleTrialId = (e) => {
    const selectedId = e.target.value;
    setFieldValue("trial_id", selectedId);

    const selectedOption = trialsList.find((trial) => trial.id === selectedId);
    setIsManual(selectedOption?.is_manual || false);
    setHasScreening(selectedOption?.has_screening_visit || false);
    setEnforceStartDate(selectedOption?.enforce_visit_start_time_at_12);
  };

  const handleChangeDateTime = (val) => {
    setDateTime(val ? dayjs(val) : null);
  };

  const getTrialsList = () => {
    let endpoint = "/api/photos/trials_add_patient/";

    if (trial_id) {
      endpoint = `/api/photos/trials_add_patient/${trial_id}/`;
    }

    loaderContext.setLoaderCount((prev) => prev + 1);
    axios
      .get(`${__API_URL__}${endpoint}`, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        if (trial_id) {
          setFieldValue("trial_id", trial_id);
          setIsManual(res?.data?.is_manual || false);
          setHasScreening(res?.data?.has_screening_visit || false);
          setEnforceStartDate(res?.data?.enforce_visit_start_time_at_12 || false);
          setTrialName(res?.data?.name || "");
        } else {
          setTrialsList(res.data);
        }
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  useEffect(() => {
    getTrialsList();
  }, []);

  return (
    <Modal
      open
      onClose={() => modalShow(false)}
      style={{ backdropFilter: "blur(10px)" }}
      aria-labelledby="contained-modal-title-vcenter"
    >
      <div>
        {loaderContext.loaderCount > 0 ? (
          <Loader />
        ) : (
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "60%",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              maxHeight: "100vh",
              overflowY: "auto",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h5" id="contained-modal-title-vcenter">
                {`Add Subject ${trialName && `(${trialName})`}`}
              </Typography>
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => modalShow(false)}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Box>

            <form onSubmit={handleSubmit}>
              <TextField
                label="Email"
                name="email"
                type="email"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                autoComplete="new-password"
                onPaste={(e) => e.preventDefault()}
                placeholder="Enter your Email address"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
                required
              />

              <TextField
                label="Confirm Email"
                name="confirm_email"
                type="email"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                autoComplete="new-password"
                onPaste={(e) => e.preventDefault()}
                placeholder="Confirm your Email address"
                value={values.confirm_email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.confirm_email && Boolean(errors.confirm_email)}
                helperText={touched.confirm_email && errors.confirm_email}
                required
              />

              <TextField
                label="First Name"
                name="first_name"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                autoComplete="new-password"
                onPaste={(e) => e.preventDefault()}
                placeholder="Enter your first name"
                value={values.first_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.first_name && Boolean(errors.first_name)}
                helperText={touched.first_name && errors.first_name}
                required
              />

              <TextField
                label="Middle Name Initial [Only One Letter]"
                name="middle_name_initial"
                inputProps={{ maxLength: 1 }}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                autoComplete="new-password"
                onPaste={(e) => e.preventDefault()}
                placeholder="Enter your middle initial"
                value={values.middle_name_initial}
                error={touched.middle_name_initial && Boolean(errors.middle_name_initial)}
                helperText={touched.middle_name_initial && errors.middle_name_initial}
                onChange={handleChange}
                onBlur={handleBlur}
              />

              <TextField
                label="Last Name"
                name="last_name"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                autoComplete="new-password"
                onPaste={(e) => e.preventDefault()}
                placeholder="Enter your last name"
                value={values.last_name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.last_name && Boolean(errors.last_name)}
                helperText={touched.last_name && errors.last_name}
                required
              />

              <Box mt={2}>
                <CustomDatePicker
                  label="Birth Date"
                  mode="date"
                  variant="outlined"
                  value={values.birth_date}
                  error={touched.birth_date && Boolean(errors.birth_date)}
                  helperText={touched.birth_date && errors.birth_date}
                  onChange={(val) => setFieldValue("birth_date", val)}
                  size="lg"
                  labelFontSize="1rem"
                  inputFontSize="1rem"
                  required={true}
                />
              </Box>

              <MuiTelInput
                label="Phone No."
                name="phone"
                fullWidth
                margin="normal"
                defaultCountry="US" // All clients are US-based for now; change when expanding internationally
                disableFormatting
                value={values.phone}
                onChange={(value) => setFieldValue("phone", value)}
                onBlur={handleBlur}
                error={touched.phone && Boolean(errors.phone)}
                helperText={touched.phone && errors.phone}
                required
              />

              <FormControl fullWidth margin="normal" required>
                <InputLabel id="preferred-language-label" shrink>
                  Preferred Language
                </InputLabel>
                <Select
                  label="Preferred Language"
                  labelId="preferred-language-label"
                  id="preferred_language"
                  name="preferred_language"
                  value={values.preferred_language}
                  onChange={handleChange}
                  onBlur={handleBlur}
                >
                  {languages.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {trial_id === null && (
                <FormControl
                  fullWidth
                  margin="normal"
                  required
                  error={touched.trial_id && Boolean(errors.trial_id)}
                >
                  <InputLabel id="trial-label" shrink>
                    Select Trial
                  </InputLabel>
                  <Select
                    label="Select Trial"
                    labelId="trial-label"
                    id="trial_id"
                    name="trial_id"
                    value={values.trial_id}
                    onChange={handleTrialId}
                    onBlur={handleBlur}
                    SelectDisplayProps={{ "data-testid": "add-subject-trial-select" }}
                  >
                    {trialsList.map((trial) => (
                      <MenuItem key={trial.id} value={trial.id}>
                        {trial.name}
                      </MenuItem>
                    ))}
                  </Select>

                  {touched.trial_id && errors.trial_id && (
                    <p
                      style={{
                        color: "red",
                        fontSize: "0.8rem",
                        marginTop: "0.25rem",
                      }}
                    >
                      {errors.trial_id}
                    </p>
                  )}
                </FormControl>
              )}
              {isManual && (
                <TextField
                  label="Subject Id"
                  name="participant_number"
                  fullWidth
                  margin="normal"
                  value={values.participant_number}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.participant_number && Boolean(errors.participant_number)}
                  helperText={touched.participant_number && errors.participant_number}
                  autoComplete="off"
                  required
                />
              )}

              <Box sx={{ mt: 2 }}>
                <CustomDatePicker
                  id="start_date"
                  name="start_date"
                  variant="outlined"
                  size="lg"
                  labelFontSize="1rem"
                  inputFontSize="1rem"
                  label={
                    hasScreening
                      ? "Start Date - This is the Start Date of the Screening Visit"
                      : "Start Date - This is the Start Date of the Baseline Visit"
                  }
                  placeholder="Select the start date and time"
                  autoComplete="new-password"
                  required={true}
                  value={dateTime}
                  onChange={handleChangeDateTime}
                  mode={enforceStartDate ? "date" : "datetime"}
                />
              </Box>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                data-testid="add-subject-submit-btn"
                disabled={isSubmitting || submitted}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                Add & Customize
              </Button>
            </form>
          </Box>
        )}
      </div>
    </Modal>
  );
};

export default AddNewPatientModal;
